/**
 * Client for engine /v1/erp (in-app workshop ERP).
 */
import { ENGINE_BASE, engineFetch } from "./config";

function baseUrl(): string {
  return (
    (typeof window !== "undefined" && (window as { __ENGINE_URL__?: string }).__ENGINE_URL__) ||
    ENGINE_BASE
  );
}

export class ErpApiError extends Error {
  status: number;
  code?: string;
  details?: Record<string, unknown>;
  constructor(status: number, message: string, code?: string, details?: Record<string, unknown>) {
    super(message);
    this.name = "ErpApiError";
    this.status = status;
    this.code = code;
    this.details = details;
  }
}

/** Map engine ERP errors to i18n keys. 503 is always hard-fail, never swallowed. */
export function erpUserMessage(err: unknown, t: (key: string) => string): string {
  if (err instanceof ErpApiError) {
    if (err.code === "erp_pull_not_configured") return t("erp.err.pullNotConfigured");
    if (err.code === "erp_pull_failed" || err.code === "erp_pull_fixture_missing") {
      return err.message || t("erp.err.pullFailed");
    }
    if (err.code === "sales_disabled") return t("erp.err.salesDisabled");
    if (err.status === 503) return t("erp.err.db");
    if (err.status === 404) return t("erp.err.disabled");
    return err.message || t("erp.err.generic");
  }
  if (err instanceof Error && err.message) return err.message;
  return t("erp.err.generic");
}

/** Shared by shopfloorApi — same base URL, auth and error shape. */
export async function jsonFetch<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await engineFetch(`${baseUrl()}${path}`, init);
  if (!res.ok) {
    let detail: unknown = res.statusText;
    try {
      detail = await res.json();
    } catch {
      /* ignore */
    }
    let code: string | undefined;
    let msg = `erp ${path} ${res.status}`;
    let details: Record<string, unknown> | undefined;
    if (detail && typeof detail === "object") {
      const d = detail as { detail?: unknown; code?: string; message?: string };
      const inner = (
        d.detail && typeof d.detail === "object"
          ? d.detail
          : d
      ) as { code?: string; message?: string; details?: Record<string, unknown> };
      if (typeof d.detail === "string") {
        msg = d.detail;
      } else {
        code = inner.code;
        msg = inner.message || msg;
        if (inner.details && typeof inner.details === "object") details = inner.details;
      }
    }
    throw new ErpApiError(res.status, msg, code, details);
  }
  return res.json() as Promise<T>;
}

export type StockCategory = "sabitler" | "islenebilir" | "hurda_talas" | "modullesmis";

export const STOCK_CATEGORIES: StockCategory[] = [
  "sabitler",
  "islenebilir",
  "hurda_talas",
  "modullesmis",
];

export interface ErpSummary {
  stock_sku_count: number;
  stock_critical_count: number;
  open_po_count: number;
  open_wo_count: number;
  movements_today: number;
  qty_on_hand_total: number;
  category_counts?: Record<string, number>;
}

export interface ErpStockItem {
  id: number | null;
  part_no: string;
  description?: string | null;
  material?: string | null;
  unit?: string;
  stock_category?: StockCategory | string;
  item_class?: string | null;
  qty_on_hand: number;
  qty_reserved: number;
  qty_available?: number;
  is_critical?: boolean;
  min_qty?: number;
  max_qty?: number;
  unit_price_try?: number;
  lead_time_days?: number;
  location?: string | null;
  supplier?: string | null;
  status?: string;
  cad_filename?: string | null;
  cad_content_type?: string | null;
  cad_size_bytes?: number | null;
  has_cad?: boolean;
  match_dimensions?: Record<string, number | string | null> | null;
}

export interface StockMovement {
  id?: string;
  stock_item_id: number;
  part_no?: string | null;
  kind: string;
  qty: number;
  balance_after: number;
  ref_type?: string | null;
  ref_id?: string | null;
  note?: string | null;
  created_at?: string | null;
}

export interface PurchaseOrderLine {
  line_no: number;
  part_no: string;
  description?: string | null;
  qty: number;
  unit_price_try: number;
  status?: string;
}

export interface PurchaseOrder {
  id?: string;
  po_no: string;
  status: string;
  source_job_id?: string | null;
  notes?: string | null;
  total_try?: number;
  lines?: PurchaseOrderLine[];
  created_at?: string | null;
}

export interface WorkOrder {
  id?: string;
  wo_no: string;
  status: string;
  source_job_id?: string | null;
  part_no?: string | null;
  qty?: number;
  route_json?: Array<Record<string, unknown>>;
  est_hours?: number | null;
  est_cost_try?: number | null;
  notes?: string | null;
  created_at?: string | null;
}

export interface JobCommitResult {
  job_id: string;
  purchase_orders: PurchaseOrder[];
  work_orders: WorkOrder[];
  reservations: Array<Record<string, unknown>>;
  skipped_fingerprints: string[];
  warnings: string[];
}

export interface ActivityItem {
  kind: string;
  id?: string;
  at?: string;
  label: string;
  detail?: Record<string, unknown>;
}

export function fetchErpSummary(): Promise<ErpSummary> {
  return jsonFetch<ErpSummary>("/v1/erp/summary");
}

export function fetchErpStock(
  includeInactive = false,
  category?: StockCategory | string | null,
): Promise<{
  items: ErpStockItem[];
  count: number;
  critical_count?: number;
  category_counts?: Record<string, number>;
}> {
  const params = new URLSearchParams();
  if (includeInactive) params.set("include_inactive", "true");
  if (category) params.set("category", category);
  const q = params.toString() ? `?${params.toString()}` : "";
  return jsonFetch(`/v1/erp/stock${q}`);
}

export interface StockCreateBody {
  part_no: string;
  description?: string | null;
  material?: string | null;
  unit?: string;
  stock_category?: StockCategory | string;
  item_class?: string | null;
  qty_on_hand?: number;
  min_qty?: number;
  max_qty?: number;
  unit_price_try?: number;
  lead_time_days?: number;
  location?: string | null;
  supplier?: string | null;
  erp_external_id?: string | null;
}

export interface StockUpdateBody {
  description?: string | null;
  material?: string | null;
  unit?: string | null;
  stock_category?: StockCategory | string | null;
  item_class?: string | null;
  min_qty?: number | null;
  max_qty?: number | null;
  unit_price_try?: number | null;
  lead_time_days?: number | null;
  location?: string | null;
  supplier?: string | null;
  erp_external_id?: string | null;
  status?: "active" | "inactive" | null;
}

export function createStock(body: StockCreateBody): Promise<ErpStockItem> {
  return jsonFetch<ErpStockItem>("/v1/erp/stock", {
    method: "POST",
    body: JSON.stringify(body),
  });
}

export function updateStock(itemId: number, body: StockUpdateBody): Promise<ErpStockItem> {
  return jsonFetch<ErpStockItem>(`/v1/erp/stock/${itemId}`, {
    method: "PATCH",
    body: JSON.stringify(body),
  });
}

export function adjustStock(
  itemId: number,
  body: { kind: string; qty: number; note?: string; allow_negative?: boolean },
): Promise<{ item: ErpStockItem; movement: StockMovement }> {
  return jsonFetch(`/v1/erp/stock/${itemId}/adjust`, {
    method: "POST",
    body: JSON.stringify(body),
  });
}

/** Attach STEP/CAD/image/PDF to a stock item. */
export async function uploadStockCad(itemId: number, file: File): Promise<ErpStockItem> {
  const form = new FormData();
  form.append("file", file, file.name);
  const res = await engineFetch(`${baseUrl()}/v1/erp/stock/${itemId}/cad`, {
    method: "POST",
    body: form,
  });
  if (!res.ok) {
    let detail: unknown = res.statusText;
    try {
      detail = await res.json();
    } catch {
      /* ignore */
    }
    const msg =
      detail && typeof detail === "object" && detail !== null && "detail" in detail
        ? JSON.stringify((detail as { detail: unknown }).detail)
        : String(detail);
    throw new ErpApiError(res.status, msg);
  }
  return res.json() as Promise<ErpStockItem>;
}

export function stockCadDownloadUrl(itemId: number): string {
  return `${baseUrl()}/v1/erp/stock/${itemId}/cad`;
}

export async function deleteStockCad(itemId: number): Promise<ErpStockItem> {
  return jsonFetch(`/v1/erp/stock/${itemId}/cad`, { method: "DELETE" });
}

export interface StockCompositionLine {
  id?: number | null;
  parent_stock_item_id: number;
  child_stock_item_id: number;
  qty: number;
  note?: string | null;
  child_part_no?: string | null;
  child_description?: string | null;
  child_stock_category?: string | null;
  child_item_class?: string | null;
  child_qty_available?: number | null;
  child_unit?: string | null;
}

export function fetchStockComposition(itemId: number): Promise<{
  parent: ErpStockItem;
  items: StockCompositionLine[];
  count: number;
  by_category: Record<string, StockCompositionLine[]>;
}> {
  return jsonFetch(`/v1/erp/stock/${itemId}/composition`);
}

export function addStockComposition(
  parentId: number,
  body: { child_stock_item_id: number; qty?: number; note?: string | null },
): Promise<StockCompositionLine> {
  return jsonFetch(`/v1/erp/stock/${parentId}/composition`, {
    method: "POST",
    body: JSON.stringify(body),
  });
}

export function updateStockComposition(
  parentId: number,
  lineId: number,
  body: { qty?: number; note?: string | null },
): Promise<StockCompositionLine> {
  return jsonFetch(`/v1/erp/stock/${parentId}/composition/${lineId}`, {
    method: "PATCH",
    body: JSON.stringify(body),
  });
}

export function removeStockComposition(parentId: number, lineId: number): Promise<{ ok: boolean }> {
  return jsonFetch(`/v1/erp/stock/${parentId}/composition/${lineId}`, {
    method: "DELETE",
  });
}

/** Replace org catalog from CSV (Stok Kodu / part_no columns). */
export async function importStockCsv(file: File): Promise<{ imported: number; organization_slug?: string }> {
  const form = new FormData();
  form.append("file", file, file.name);
  const res = await engineFetch(`${baseUrl()}/v1/erp/stock/import`, {
    method: "POST",
    body: form,
  });
  if (!res.ok) {
    let detail: unknown = res.statusText;
    try {
      detail = await res.json();
    } catch {
      /* ignore */
    }
    throw new ErpApiError(res.status, `import failed: ${JSON.stringify(detail)}`);
  }
  return res.json() as Promise<{ imported: number; organization_slug?: string }>;
}

export function fetchMovements(limit = 80): Promise<{ items: StockMovement[]; count: number }> {
  return jsonFetch(`/v1/erp/stock/movements?limit=${encodeURIComponent(String(limit))}`);
}

export function fetchPurchaseOrders(limit = 50): Promise<{ items: PurchaseOrder[]; count: number }> {
  return jsonFetch(`/v1/erp/purchase-orders?limit=${encodeURIComponent(String(limit))}`);
}

export interface PurchaseOrderCreateBody {
  lines: Array<{
    part_no: string;
    description?: string | null;
    qty: number;
    unit_price_try?: number;
    stock_item_id?: number | null;
  }>;
  notes?: string | null;
  source_job_id?: string | null;
}

/** Manual PO draft — the commit flow is not the only way to buy something. */
export function createPurchaseOrder(body: PurchaseOrderCreateBody): Promise<PurchaseOrder> {
  return jsonFetch(`/v1/erp/purchase-orders`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
}

export function submitPurchaseOrder(poId: string): Promise<PurchaseOrder> {
  return jsonFetch(`/v1/erp/purchase-orders/${encodeURIComponent(poId)}/submit`, {
    method: "POST",
    body: JSON.stringify({}),
  });
}

export function receivePurchaseOrder(poId: string): Promise<PurchaseOrder> {
  return jsonFetch(`/v1/erp/purchase-orders/${encodeURIComponent(poId)}/receive`, {
    method: "POST",
    body: JSON.stringify({}),
  });
}

export function fetchWorkOrders(limit = 50): Promise<{ items: WorkOrder[]; count: number }> {
  return jsonFetch(`/v1/erp/work-orders?limit=${encodeURIComponent(String(limit))}`);
}

export function releaseWorkOrder(woId: string): Promise<WorkOrder> {
  return jsonFetch(`/v1/erp/work-orders/${encodeURIComponent(woId)}/release`, {
    method: "POST",
    body: JSON.stringify({}),
  });
}

export function completeWorkOrder(woId: string): Promise<WorkOrder> {
  return jsonFetch(`/v1/erp/work-orders/${encodeURIComponent(woId)}/complete`, {
    method: "POST",
    body: JSON.stringify({}),
  });
}

export function commitJobToErp(
  jobId: string,
  opts?: { force?: boolean },
): Promise<JobCommitResult> {
  return jsonFetch(`/v1/erp/jobs/${encodeURIComponent(jobId)}/commit`, {
    method: "POST",
    body: JSON.stringify({ force: !!opts?.force }),
  });
}

export function fetchJobErpDocuments(jobId: string): Promise<Record<string, unknown>> {
  return jsonFetch(`/v1/erp/jobs/${encodeURIComponent(jobId)}/documents`);
}

export function fetchErpActivity(limit = 30): Promise<{ items: ActivityItem[]; count: number }> {
  return jsonFetch(`/v1/erp/activity?limit=${encodeURIComponent(String(limit))}`);
}

export interface ErpSyncPullConflict {
  part_no: string;
  on_hand: number;
  external_qty: number;
  delta: number;
}

export interface ErpSyncPullMatch {
  part_no: string;
  qty_on_hand: number;
  external_id?: string;
}

export interface ErpSyncPullResult {
  adapter: string;
  read_only: boolean;
  conflict_count: number;
  matches: ErpSyncPullMatch[];
  conflicts: ErpSyncPullConflict[];
  missing_local: Array<{ part_no: string; external_qty?: number }>;
  missing_external: Array<{ part_no: string; on_hand?: number }>;
  pulled_at?: string;
}

/** Read-only external stock snapshot vs local on_hand. Never overwrites qty. */
export function postErpSyncPull(): Promise<ErpSyncPullResult> {
  return jsonFetch<ErpSyncPullResult>("/v1/erp/sync/pull", {
    method: "POST",
    body: JSON.stringify({}),
  });
}

export interface SalesOrder {
  id: string;
  so_no: string;
  status: string;
  rfq_id?: string | null;
  customer_name?: string | null;
  customer_email?: string | null;
  total_try?: number;
  lines?: Array<{
    id?: string;
    line_no: number;
    part_no?: string | null;
    description?: string | null;
    qty: number;
    unit?: string;
    qty_delivered?: number;
    fulfillment?: string;
    line_total_try?: number;
    stock_item_id?: number | null;
  }>;
}

export interface DeliveryNote {
  id: string;
  dn_no: string;
  status: string;
  sales_order_id: string;
  recipient_snapshot?: Record<string, unknown>;
  shipment_snapshot?: Record<string, unknown>;
  lines?: Array<{
    id?: string;
    line_no: number;
    part_no?: string | null;
    qty: number;
    unit?: string;
    stock_item_id?: number | null;
    reservation_id?: string | null;
    sales_order_line_id?: string | null;
  }>;
}

export function fetchSalesOrders(limit = 50): Promise<{ items: SalesOrder[]; count: number }> {
  return jsonFetch(`/v1/erp/sales-orders?limit=${encodeURIComponent(String(limit))}`);
}

export function cancelSalesOrder(soId: string): Promise<SalesOrder> {
  return jsonFetch(`/v1/erp/sales-orders/${encodeURIComponent(soId)}/cancel`, { method: "POST" });
}

export function fetchDeliveryNotes(
  salesOrderId?: string,
  limit = 50,
): Promise<{ items: DeliveryNote[]; count: number }> {
  const q = new URLSearchParams({ limit: String(limit) });
  if (salesOrderId) q.set("sales_order_id", salesOrderId);
  return jsonFetch(`/v1/erp/delivery-notes?${q.toString()}`);
}

export function createDeliveryNote(body: Record<string, unknown>): Promise<DeliveryNote> {
  return jsonFetch("/v1/erp/delivery-notes", { method: "POST", body: JSON.stringify(body) });
}

export function updateDeliveryNote(dnId: string, body: Record<string, unknown>): Promise<DeliveryNote> {
  return jsonFetch(`/v1/erp/delivery-notes/${encodeURIComponent(dnId)}`, {
    method: "PATCH",
    body: JSON.stringify(body),
  });
}

export function shipDeliveryNote(dnId: string): Promise<DeliveryNote> {
  return jsonFetch(`/v1/erp/delivery-notes/${encodeURIComponent(dnId)}/ship`, { method: "POST" });
}

export function cancelDeliveryNote(dnId: string): Promise<DeliveryNote> {
  return jsonFetch(`/v1/erp/delivery-notes/${encodeURIComponent(dnId)}/cancel`, { method: "POST" });
}

export async function fetchDeliveryNotePdf(dnId: string): Promise<Blob> {
  const res = await engineFetch(`${baseUrl()}/v1/erp/delivery-notes/${encodeURIComponent(dnId)}/pdf`);
  if (!res.ok) {
    throw new ErpApiError(res.status, `delivery note pdf ${res.status}`);
  }
  return res.blob();
}
